import { george, i18n, pr, theme } from "aaa";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { IconBspOptionsProlongation, IconBuildingSavingsContractXl, IconContractPayoutXl, IconMoney } from "components/util/primitives";
import { ui } from "components/util/uiHelpers";
import { FormattedHTMLMessage } from "i18n/util";
import { observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import auth from "stores/AuthStore";
import externalInterfaceStore, { IDeeplinkState } from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { IOffer } from "types/offer";
import { performStoreHandoverTokenExchange } from "util/storeHandover";
import { BSPOptionsRoutes } from "./BSPOptionsRoutes";
import { BSPOptionsStore } from "./BSPOptionsStore";
import { BSPOptionsAppointmentCard } from "./components/BSPOptionsAppointmentCard";
import { BSPOptionsInfoBox } from "./components/BSPOptionsInfoBox";

type IProps = RouteComponentProps<{ id: string }> & i18n.InjectedIntlProps & {
    store: BSPOptionsStore;
};

const ActionCard = observer(({
    title,
    content,
    icon,
    actionButtonText,
    onClickActionButton,
    isMobile,
}: {
    title: string,
    content: React.ReactNode,
    icon: React.ReactNode,
    actionButtonText: string,
    onClickActionButton: () => void,
    isMobile?: boolean,
}) => (
    <george.Card
        disableTopSpacing
        style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row",
            borderRadius: 8,
            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)"
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#028661",
                padding: "24px 30px",
                borderRadius: isMobile ? "8px 8px 0px 0px" : "8px 0px 0px 8px"
            }}
        >
            {icon}
        </div>
        <div style={{ padding: 24 }}>
            <pr.P_18_TITLE style={{ lineHeight: "24px", fontWeight: 700 }}>
                {title}
            </pr.P_18_TITLE>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, lineHeight: "21px", marginTop: 9 }}>
                {content}
            </div>
        </div>
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                borderRadius: isMobile ? "0px 0px 8px 8px" : "0px 8px 8px 0px",
                padding: 24,
                paddingLeft: isMobile ? 24 : 0
            }}
        >
            <GdsFakeActionButton color="primary" onClick={onClickActionButton}>
                {actionButtonText}
            </GdsFakeActionButton>
        </div>
    </george.Card>
));

const getHandoverUrl = (offer?: IOffer) => {
    const initialHandoverUrl = offer?.handover?.url;

    if (!initialHandoverUrl) {
        return;
    }

    const stateObject: IDeeplinkState = {
        srcClientId: externalInterfaceStore.clientId,
        returnState: "/overview",
        redirectUri: externalInterfaceStore.georgeBaseUrl + "/?at=c"
    };

    // Base64 encode the state object -> same as in AssetCalculation
    return `${initialHandoverUrl}${initialHandoverUrl.includes("?") ? "&" : "?"}state=${btoa(JSON.stringify(stateObject))}`;
};

export const BSPOptionsOverview = observer(({ store }: IProps) => {

    const handleHandover = () => {
        const url = getHandoverUrl(store.buildingsOffer);
        if (!url) {
            pushRoute("/error");
            return;
        }

        performStoreHandoverTokenExchange(url);
    };

    const handleClickNewContract = () => {
        handleHandover();
    };

    const handleClickNewContractAndPayout = () => {
        handleHandover();
    };

    const handleClickNewContractAndPayout30 = () => {
        handleHandover();
    };

    const handleClickContinueWithBonus = () => {
        store.selectedOption = "continueWithBonus";
        store.selectedPayout = undefined;

        trackingStore.trackEvent({ eventName: "bspOptionsPlus3Screen", productKey: "PLUS3" });
        pushRoute(BSPOptionsRoutes.DETAILS);
    };

    const handleClickContinueWithBonusLegalGuardian = () => {
        store.selectedOption = "continueWithBonusLegalGuardian";
        store.selectedPayout = undefined;

        trackingStore.trackEvent({ eventName: "bspOptionsPlus3Screen", productKey: "PLUS3GV" });
        pushRoute(BSPOptionsRoutes.DETAILS);
    };

    const handleClickPayout = () => {
        // TODO: remove if the the same information is provided by possible code function
        store.selectedPayout = "payout";
        trackingStore.trackEvent({ eventName: "bspOptionsPlus3Screen", productKey: "PAYOUT" });
        pushRoute(BSPOptionsRoutes.PAYOUT);
    };

    const handleClickPayoutLegalGuardian = () => {
        // TODO: remove if the the same information is provided by possible code function
        store.selectedPayout = "payoutLegalGuardian";
        trackingStore.trackEvent({ eventName: "bspOptionsPlus3Screen", productKey: "PAYOUTGV" });
        pushRoute(BSPOptionsRoutes.PAYOUT);
    };

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;
    const hasContractOptions = store.canMakeNewContract
        || store.canMakeNewContractAndPayout
        || store.canMakeNewContractAndPayout30
        || store.canContinueWithBonus
        || store.canContinueWithBonusLegalGuardian
        || store.canMakePayout
        || store.canMakePayoutLegalGuardian;

    const expiryDate = store.buildingsAccount?.saving.expiryDate ? new Date(store.buildingsAccount?.saving.expiryDate) : null;
    const localizedExpiryDate = expiryDate ? expiryDate.toLocaleDateString(auth.locale, { day: "numeric", month: "long", year: "numeric" }) : "-";

    const isExpiryDateInFutureOrToday = expiryDate && moment(expiryDate).isSameOrAfter(moment(), "day");

    return (
        <BackgroundComponent style={{ paddingTop: 48, paddingBottom: theme.MARGIN }}>
            {store.buildingsAccount && (
                <>
                    {hasContractOptions && (
                        <>
                            <div style={pr.STYLE_CENTERED_CARD}>
                                <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                                    {store.buildingsAccount.saving.expiryDate && ui.__(
                                        moment(store.buildingsAccount.saving.expiryDate).isAfter(moment())
                                            ? "bspOptions.overview.info.withDate"
                                            : "bspOptions.overview.info.withDate.past",
                                        {
                                            contractNumber: store.buildingsAccount.id,
                                            date: moment(store.buildingsAccount.saving.expiryDate).format("DD.MM.YYYY")
                                        }
                                    )}
                                    {!store.buildingsAccount.saving.expiryDate && ui.__("bspOptions.overview.info.withoutDate")}
                                </pr.P_22_TITLE>
                                <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                                    {ui.__("bspOptions.overview.intro")}
                                </pr.P_16_CONTENT>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 32 }}>
                                {store.canContinueWithBonus && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.continueWithBonus.title")}
                                        content={(
                                            <pr.P_14_CONTENT>
                                                <FormattedHTMLMessage id="bspOptions.overview.card.continueWithBonus.text" />
                                            </pr.P_14_CONTENT>
                                        )}
                                        icon={<IconBspOptionsProlongation style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.continueWithBonus.button")}
                                        onClickActionButton={handleClickContinueWithBonus}
                                    />
                                )}
                                {store.canContinueWithBonusLegalGuardian && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.continueWithBonusLegalGuardian.title")}
                                        content={(
                                            <pr.P_14_CONTENT>
                                                <FormattedHTMLMessage id="bspOptions.overview.card.continueWithBonusLegalGuardian.text" />
                                            </pr.P_14_CONTENT>
                                        )}
                                        icon={<IconBspOptionsProlongation style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.continueWithBonusLegalGuardian.button")}
                                        onClickActionButton={handleClickContinueWithBonusLegalGuardian}
                                    />
                                )}
                                {store.canMakeNewContractAndPayout && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.newContractAndPayout.title")}
                                        content={(
                                            <pr.P_14_CONTENT>
                                                <FormattedHTMLMessage id="bspOptions.overview.card.newContractAndPayout.text" />
                                            </pr.P_14_CONTENT>
                                        )}
                                        icon={<IconContractPayoutXl style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.newContractAndPayout.button")}
                                        onClickActionButton={handleClickNewContractAndPayout30}
                                    />
                                )}
                                {store.canMakeNewContractAndPayout30 && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.newContractAndPayout30.title")}
                                        content={(
                                            <pr.P_14_CONTENT>
                                                <FormattedHTMLMessage id="bspOptions.overview.card.newContractAndPayout30.text" />
                                            </pr.P_14_CONTENT>
                                        )}
                                        icon={<IconContractPayoutXl style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.newContractAndPayout30.button")}
                                        onClickActionButton={handleClickNewContractAndPayout}
                                    />
                                )}
                                {store.canMakeNewContract && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.newContract.title")}
                                        content={(
                                            <pr.P_14_CONTENT>
                                                <FormattedHTMLMessage id="bspOptions.overview.card.newContract.text" />
                                            </pr.P_14_CONTENT>
                                        )}
                                        icon={<IconBuildingSavingsContractXl style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.newContract.button")}
                                        onClickActionButton={handleClickNewContract}
                                    />
                                )}
                                {store.canMakePayout && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.payout.title")}
                                        content={(
                                            <>
                                                <pr.P_14_CONTENT>
                                                    <FormattedHTMLMessage id="bspOptions.overview.card.payout.text" />
                                                </pr.P_14_CONTENT>
                                                {isExpiryDateInFutureOrToday && <pr.P_14_CONTENT>
                                                    <FormattedHTMLMessage id="bspOptions.overview.card.payout.subtext" values={{ date: localizedExpiryDate }} />
                                                </pr.P_14_CONTENT>}
                                            </>
                                        )}
                                        icon={<IconMoney style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.payout.button")}
                                        onClickActionButton={handleClickPayout}
                                    />
                                )}
                                {store.canMakePayoutLegalGuardian && (
                                    <ActionCard
                                        isMobile={isMobile}
                                        title={ui.__("bspOptions.overview.card.payoutLegalGuardian.title")}
                                        content={(
                                            <>
                                                <pr.P_14_CONTENT>
                                                    <FormattedHTMLMessage id="bspOptions.overview.card.payoutLegalGuardian.text" />
                                                </pr.P_14_CONTENT>
                                                {isExpiryDateInFutureOrToday && <pr.P_14_CONTENT>
                                                    <FormattedHTMLMessage id="bspOptions.overview.card.payoutLegalGuardian.subtext" values={{ date: localizedExpiryDate }} />
                                                </pr.P_14_CONTENT>}
                                            </>
                                        )}
                                        icon={<IconMoney style={{ width: 64, height: 64, color: "white" }} />}
                                        actionButtonText={ui.__("bspOptions.overview.card.payoutLegalGuardian.button")}
                                        onClickActionButton={handleClickPayoutLegalGuardian}
                                    />
                                )}
                            </div>
                            {store.canBookAppointment && (
                                <BSPOptionsAppointmentCard isMobile={isMobile} style={{ marginTop: 32 }} />
                            )}
                            <BSPOptionsInfoBox style={{ marginTop: 16 }} >
                                <pr.P_16_CONTENT>
                                    {ui.__("bspOptions.overview.infoBox.withOptions")}
                                </pr.P_16_CONTENT>
                            </BSPOptionsInfoBox>
                        </>
                    )}
                    {!hasContractOptions && (
                        <BSPOptionsInfoBox>
                            <pr.P_16_CONTENT>
                                {ui.__("bspOptions.overview.infoBox.withoutOptions")}
                            </pr.P_16_CONTENT>
                        </BSPOptionsInfoBox>
                    )}
                </>
            )}
            {!store.buildingsAccount && (
                <BSPOptionsInfoBox>
                    <pr.P_16_CONTENT>
                        {ui.__("bspOptions.overview.infoBox.invalidContract")}
                    </pr.P_16_CONTENT>
                </BSPOptionsInfoBox>
            )}
        </BackgroundComponent>
    );
});



// WEBPACK FOOTER //
// ./src/components/BSPOptions/BSPOptionsOverview.tsx